import { useEffect, useState } from "react"

import { CellProps, useFocusManagement } from "components/TreeState"
import { subscribe, useSnapshot } from "valtio"

import { Bulky } from "./Bulky"
import { CellError } from "./CellError"
import Input from "ui/inputs/Input"
import { SpotsPlan } from "../types"
import { checkBuyingStatus } from "utils/type-guards"
import { css } from "@emotion/css"
import { get } from "lodash"
import { useCellDialog } from "../useCellDialog"
import { useI18n } from "providers/I18n"
import { useTableSource } from "../TableStateProvider"

export function SpotsPlanCell(props: CellProps) {
  const { rowId: id, dataKey, columnId, disabled } = props
  const { t } = useI18n()
  const tableSource = useTableSource()
  const readFrom = useSnapshot(tableSource.state)
  const readError = useSnapshot(tableSource.errors)
  const [plotted, setPlotted] = useState<number>()
  const writeTo = tableSource.state
  const { onChange } = useCellDialog()

  const values = readFrom[id] || {}
  const { type } = values
  const readonly = type !== "line_item"

  const error = get(readError, `${id}.${columnId}`)
  const { maxNumberOfSpots = 0 } = (get(readFrom, `${id}.${dataKey}`) ||
    {}) as SpotsPlan

  const { getInputProps } = useFocusManagement()
  const { onBlur } = getInputProps()

  const buyingStatusVal = String(get(readFrom, `${id}.buyingStatus`))
  const buyingStatus = checkBuyingStatus(buyingStatusVal) ? buyingStatusVal : ""

  useEffect(() => {
    const getPlotted = () => {
      const { spots = [] } = (get(writeTo[id], dataKey) || {}) as SpotsPlan
      const sum = spots.reduce((sum, curr) => {
        sum += Number(curr.quantity || 0)
        return sum
      }, 0)

      setPlotted(sum)
    }
    getPlotted()

    const unsub = subscribe(writeTo, getPlotted)

    return () => unsub()
  }, [dataKey, writeTo, id])

  const onOpenSpotPlans = () =>
    onChange({ type: "spots_plan", fieldInfo: { rowId: id, dataKey } })

  const isDisabled = buyingStatus === "POSTED_FOR_BILLING"

  return readonly ? (
    <span />
  ) : (
    <div className={styles.wrapper}>
      <Bulky {...props} otherDisabled={!!readonly}>
        <div className={styles.primary}>
          <div className={styles.primaryInput}>
            <span className="BodyBody-xsDark-greyRegular">{t("plotted")}</span>
            <span className="umbersNumber-2_rightDark-greyRegular">
              {plotted}
            </span>
          </div>
          <Input
            readOnly
            disabled={isDisabled}
            onBlur={onBlur}
            value={maxNumberOfSpots}
            style={{ textAlign: "end" }}
            autoComplete="off"
            type="tel"
            onClick={() => !disabled && onOpenSpotPlans()}
          />
        </div>
      </Bulky>
      {!!error ? <CellError error={error} /> : null}
    </div>
  )
}

const styles = {
  wrapper: css`
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
  `,
  title: css`
    min-width: 62px;
  `,
  primary: css`
    display: flex;
    width: 100%;
    font-size: 13px;
    > * {
      width: 100%;
    }
  `,
  primaryInput: css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    max-width: var(--spacing-xxl-6);
  `,
}
